import type {
  ChromeLink,
  ChromeLinkSection,
  GlobalNavItem,
  SidebarBanner,
  SidebarNavItem,
} from '@/types/domains/ChromeType';
// お役立ちコラムの遷移先は `ColumnConst` が唯一の定義（トップのセクションと同じ値を使う）
import { COLUMN_URL } from '@/consts/ColumnConst';

/**
 * デザインリニューアル2026 の共通chrome の静的リンク定義。
 * 商品リスト（カテゴリ別）は API 由来なのでここには持たず `useProductContent` から取る（決定#5・§3.7）。
 */

/**
 * コーポレートサイト。ヘッダーのグローバルナビとフッタ「コンテンツ」の共通の飛び先（決定#28）。
 * 支給デザインはフッタ側だけラベルが「オリエンタルバイオの想い」だが、**表記揺れなのでナビと同じ
 * 「オリエンタルバイオとは？」に揃える**（2026-08-10 判断）。飛び先はもとから同一。
 */
export const CORPORATE_SITE_URL = 'https://www.orientalbio.jp/' as const;

/** トップの「人気の商品ランキング」の要素ID（`organisms/index/products.vue` の `id="products"`） */
export const PRODUCTS_ANCHOR_HASH = '#products' as const;

/** トップの「人気の商品ランキング」へのページ内アンカー（決定#26・§3.4。アンカー実体は PR-D で付けた） */
export const PRODUCTS_ANCHOR = `/${PRODUCTS_ANCHOR_HASH}` as const;

/**
 * 「人気の商品ランキング」のタブを指定するクエリキー。値は `CATEGORY_ENGLISH_NAME` のカテゴリ英名。
 * ハンバーガーのカテゴリはこれを付けて飛び、タブを押すと `products.vue` がこれを書き換える。
 * 指定が無い／未知の値なら「すべて」になる。
 */
export const PRODUCTS_CATEGORY_QUERY = 'category' as const;

/** グローバルナビ5項目 */
export const GLOBAL_NAV_ITEMS: ReadonlyArray<GlobalNavItem> = [
  {
    label: 'オリエンタルバイオとは？',
    labelLines: ['オリエンタル', 'バイオとは？'],
    to: CORPORATE_SITE_URL,
    external: true,
    icon: '/image/icon_orientalbio_red.png',
    iconModifier: 'orientalbio',
  },
  {
    label: '製品一覧',
    labelLines: ['製品', '一覧'],
    to: PRODUCTS_ANCHOR,
    icon: '/image/icon_supplement_red.png',
  },
  {
    label: 'ご愛用者様の声',
    labelLines: ['ご愛用者', '様の声'],
    to: '/voice',
    icon: '/image/icon_voice_red.png',
  },
  {
    label: 'お役立ちコラム',
    labelLines: ['お役立ち', 'コラム'],
    to: COLUMN_URL,
    external: true,
    icon: '/image/icon_column_red.png',
    iconModifier: 'column',
  },
  {
    label: 'お客様サポート',
    labelLines: ['お客様', 'サポート'],
    to: '/guide',
    icon: '/image/icon_support_red.png',
  },
] as const;

/** お買い物サポート。フッター・ハンバーガーの両方で使う */
export const SHOPPING_SUPPORT_LINKS: ReadonlyArray<ChromeLink> = [
  { label: '会員登録', to: '/register' },
  { label: 'はじめての方へ', to: '/welcome' },
  { label: 'お買い物ガイド', to: '/guide' },
  { label: '定期コース特典', to: '/regular' },
  { label: 'よくあるご質問', to: '/faq' },
  { label: 'お問い合わせ', to: '/contact' },
] as const;

/** コンテンツ。フッター・ハンバーガーの両方で使う */
export const CONTENTS_LINKS: ReadonlyArray<ChromeLink> = [
  { label: 'オリエンタルバイオとは？', to: CORPORATE_SITE_URL, external: true },
  { label: 'ご愛用者様の声', to: '/voice' },
  { label: 'お役立ちコラム', to: COLUMN_URL, external: true },
  { label: 'お知らせ', to: '/news' },
] as const;

/** カテゴリ以外のアコーディオン2段。フッター（SP）・ハンバーガーで共通 */
export const CHROME_LINK_SECTIONS: ReadonlyArray<ChromeLinkSection> = [
  { id: 'shopping-support', title: 'お買い物サポート', links: [...SHOPPING_SUPPORT_LINKS] },
  { id: 'contents', title: 'コンテンツ', links: [...CONTENTS_LINKS] },
] as const;

/**
 * 個人情報まわりのコーポレートサイト側ページ。**フッターと本文で同じ値を使う**（決定#78）。
 *
 * `nuxt.config.ts` の `NUXT_PUBLIC_PRIVACY_URL` / `NUXT_PUBLIC_PUBLISHED_URL` は
 * デプロイ時にS3から降る `.env` に記載が無く、`to` が `undefined` になって飛べない
 * （現行本番でも同じ）。env を直すのはインフラ側の作業になるのでここを正とする。
 */
export const PRIVACY_POLICY_URL = 'https://www.orientalbio.jp/privacy/' as const;
export const PUBLISHED_MATTERS_URL = 'https://www.orientalbio.jp/published/' as const;

/** フッター最下段のリンク */
export const FOOTER_BOTTOM_LINKS: ReadonlyArray<ChromeLink> = [
  { label: '企業情報', to: 'https://www.orientalbio.jp/company/', external: true },
  { label: '利用規約', to: 'https://www.orientalbio.jp/terms/', external: true },
  { label: '個人情報保護方針', to: PRIVACY_POLICY_URL, external: true },
  {
    label: '個人情報取り扱いに関する公表事項',
    to: PUBLISHED_MATTERS_URL,
    external: true,
  },
  { label: '特定商取引法に基づく表記', to: '/tokutei' },
  { label: 'カスタマーハラスメントに対する基本方針', to: '/customer-harassment-policy' },
] as const;

/** サイドバーのナビ3項目 */
export const SIDEBAR_NAV_ITEMS: ReadonlyArray<SidebarNavItem> = [
  {
    label: '初めての方へ',
    to: '/welcome',
    icon: '/image/common/sidebar/icon_beginner_red.png',
    iconModifier: 'beginner',
  },
  {
    label: 'よくあるご質問',
    to: '/faq',
    icon: '/image/common/sidebar/icon_faq_red.png',
    iconModifier: 'faq',
  },
  {
    label: 'お買い物ガイド',
    to: '/guide',
    icon: '/image/common/sidebar/icon_guide_red.png',
    iconModifier: 'guide',
  },
] as const;

/**
 * サイドバーのバナー3枚。
 *
 * **CSR活動バナーは飛び先が未確定なので出さない**（#1351・§8-29-①）。飛び先が決まったら
 * `to` を入れてこの配列へ戻す（`src/public/image/common/sidebar/s-banner_csr.png` は残してある）。
 */
export const SIDEBAR_BANNERS: ReadonlyArray<SidebarBanner> = [
  {
    to: '/set',
    src: '/image/common/sidebar/s-banner_set.png',
    alt: '大人気サプリがセットでお得！',
  },
  {
    to: CORPORATE_SITE_URL,
    src: '/image/common/sidebar/s-banner_corporate.png',
    alt: 'OrientalBio 企業サイト',
    external: true,
  },
  {
    to: 'https://www.youtube.com/watch?v=JvLjRz_IK3g',
    src: '/image/common/sidebar/s-banner_youtube.png',
    alt: 'OrientalBio 公式youtube',
    external: true,
  },
] as const;
